/**
 * Angular Downgraded Components
 *
 * This file registers Angular components for use in AngularJS templates.
 *
 * Usage in AngularJS template:
 * ```html
 * <ng-alert type="warning">Warning message</ng-alert>
 * <ng-spinner></ng-spinner>
 * ```
 *
 * Call registerDowngradedComponents() after Angular bootstrap.
 */
import { downgradeComponent, downgradeInjectable } from '@angular/upgrade/static';
import * as angular from 'angular';

// Import Angular components to downgrade
import { AddonTypeSelectorComponent } from '../shared/components/addon-type-selector/addon-type-selector.component';
import { AlertComponent } from '../shared/components/alert/alert.component';
import { CheckboxComponent } from '../shared/components/checkbox/checkbox.component';
import { DateRangeComponent } from '../shared/components/date-range/date-range.component';
import { FormInputComponent } from '../shared/components/form-input/form-input.component';
import { InputFieldComponent } from '../shared/components/input-field/input-field.component';
import { InputFieldWithLabelComponent } from '../shared/components/input-field-with-label/input-field-with-label.component';
import { PasswordRequirementsComponent } from '../shared/components/password-requirements/password-requirements.component';
import { ReservationInputComponent } from '../shared/components/reservation-input/reservation-input.component';
import { TicketTypeSelectorComponent } from '../shared/components/ticket-type-selector/ticket-type-selector.component';
import { ExampleComponent } from '../features/example/example.component';
import { SpinnerComponent } from '../shared/components/spinner/spinner.component';
import { HeaderMinimalComponent } from '../shared/components/header-minimal/header-minimal.component';
import { HeaderPrintComponent } from '../shared/components/header-print/header-print.component';
import { HeaderAppComponent } from '../shared/components/header-app/header-app.component';
import { ZendeskLoginComponent } from '../features/zendesk-login/zendesk-login.component';
import { ZendeskLogoutComponent } from '../features/zendesk-logout/zendesk-logout.component';
import { GiftCardsComponent } from '../features/gift-cards/gift-cards.component';
// import { LoaderComponent } from '../shared/components/loader/loader.component';

// Import Angular services to downgrade
import { ApiService } from '../core/api/api.service';
import { ImportExportsApiService } from '../core/api/import-exports/import-exports-api.service';
import { ImportJobExecutionsApiService } from '../core/api/import-job-executions/import-job-executions-api.service';
import { ImportJobsApiService } from '../core/api/import-jobs/import-jobs-api.service';
import { OfflineService } from '../core/api/offline/offline.service';
import { PosCloudConfigurationService } from '../core/api/pos-cloud-configuration/pos-cloud-configuration.service';
import { AuthenticationService } from '../core/auth/authentication.service';
import { BugsnagService } from '../core/bugsnag/bugsnag.service';
import { ButtonGroupActionsService } from '../core/button-group-actions/button-group-actions.service';
import { CalendarService } from '../core/calendar/calendar.service';
import { DatePickerNoteService } from '../core/date-picker-note/date-picker-note.service';
import { GanttTransformService } from '../core/gantt-transform/gantt-transform.service';
import { PendoService } from '../core/pendo/pendo.service';
import { ConfigService } from '../core/config/config.service';
import { ContentHeaderService } from '../core/content-header/content-header.service';
import { FeatureToggleService } from '../core/feature-toggle/feature-toggle.service';
import { HelpersService } from '../core/helpers/helpers.service';
import { LoggerService } from '../core/logger/logger.service';
import { ThemesHelperService } from '../core/themes-helper/themes-helper.service';
import { UserPreferencesService } from '../core/user-preferences/user-preferences.service';
import { LunchLitelyService } from '../core/lunch-litely/lunch-litely.service';
import { NavigationService } from '../core/navigation/navigation.service';
import { PermissionsService } from '../core/permissions/permissions.service';
import { SidebarService } from '../core/sidebar/sidebar.service';
import { StorageService } from '../core/storage/storage.service';
import { TenantService } from '../core/tenant/tenant.service';
import { UtilsService } from '../core/utils/utils.service';
import { ValidateService } from '../core/validate/validate.service';
import { ViewStateService } from '../core/view-state/view-state.service';
import { DiscountsService } from '../core/discounts/discounts.service';
import { StripeService } from '../core/stripe/stripe.service';
import { PaymentProcessorService } from '../core/payment-processor/payment-processor.service';
import { OrderService } from '../core/order-service/order-service.service';
import { PaymentSettingsService } from '../core/payment-settings/payment-settings.service';
import { SalesRulesService } from '../core/sales-rules/sales-rules.service';
import { OrderEditSalesRulesService } from '../core/order-edit-sales-rules/order-edit-sales-rules.service';
import { ResourcesService } from '../core/resources/resources.service';
import { AuditTrailService } from '../core/audit-trail/audit-trail.service';
import { AuditTrailConfigsService } from '../core/audit-trail/audit-trail-configs.service';
import { AuditTrailGridsService } from '../core/audit-trail/audit-trail-grids.service';
import { AuditTrailColumnGenerator } from '../core/audit-trail/audit-trail-column-generator.service';
import { AuditTrailVersionHandlerService } from '../core/audit-trail/audit-trail-version-handler.service';
import { TicketConfigService } from '../core/ticket-config/ticket-config.service';
import { TerminalsService } from '../core/terminals/terminals.service';
import { VenueService } from '../core/venue/venue.service';
import { RulesEngineService } from '../core/rules-engine/rules-engine.service';
import { ContactService } from '../core/contact/contact.service';
import { CustomerService } from '../core/customer/customer.service';
import { ModalService } from '../core/modal/modal.service';
import { AddressService } from '../core/address/address.service';
import { LocationService } from '../core/address/location.service';
import { FeatureService } from '../core/feature/feature.service';
import { MembershipService } from '../core/membership/membership.service';
import { MembershipOfferingService } from '../core/membership/offering/offering.service';
import { MembershipCardholderService } from '../core/membership/cardholder/membership-cardholder.service';
import { ReportsService } from '../core/reports/reports.service';
import { PosConfigService } from '../core/pos-config/pos-config.service';
import { TemplatesHelpersService } from '../core/templates/helpers/templates-helpers.service';
import { FlexpayService } from '../core/templates/flexpay/flexpay.service';
import { TemplatesService } from '../core/templates/templates.service';
import { CartCouponsService } from '../core/cart/coupons/cart-coupons.service';
import { CartEventsService } from '../core/cart/events/cart-events.service';
import { CartMembershipsService } from '../core/cart/memberships/cart-memberships.service';
import { CartService } from '../core/cart/cart.service';
import { PaymentService } from '../core/payment/payment.service';
import { CheckoutProcessService } from '../core/checkout-process/checkout-process.service';
import { PrintService } from '../core/print/print.service';

/**
 * Register all downgraded Angular components with AngularJS
 *
 * Call this function after Angular has bootstrapped.
 * Components will be available in AngularJS templates with 'ng-' prefix.
 */
export function registerDowngradedComponents(): void {
  const acmeModule = angular.module('acme');

  // ============================================
  // Downgraded Components
  // ============================================

  // AlertComponent - migrated from acme-alert
  // Usage: <ng-alert type="warning">Message</ng-alert>
  acmeModule.directive('ngAlert', downgradeComponent({ component: AlertComponent }));

  // Also register under the legacy `acmeAlert` name so the existing
  // <acme-alert> consumers keep working after the AngularJS component is removed.
  // Note: inputs are now bound as literals/interpolation (e.g. type="error"),
  // not AngularJS `<` expressions (type="'error'").
  acmeModule.directive('acmeAlert', downgradeComponent({ component: AlertComponent }));

  // CheckboxComponent - migrated from acme-checkbox
  // Usage: <ng-checkbox checked="$ctrl.value" disabled="$ctrl.disabled">Label</ng-checkbox>
  acmeModule.directive(
    'acmeCheckbox',
    downgradeComponent({
      component: CheckboxComponent,
      inputs: ['checked', 'disabled', 'indeterminate'],
      outputs: ['checkedChange'],
    }),
  );

  // DateRangeComponent - migrated from acme-date-range
  // Usage: <ng-date-range mode="'custom'" initial-range-type="'current_year'"
  //                       value="$ctrl.query" on-value-change="$ctrl.query = $event"></ng-date-range>
  // The original was an ng-model form control; consumers now use the explicit
  // value/valueChange two-way binding (the downgrade does not wire ng-model).
  acmeModule.directive(
    'acmeDateRange',
    downgradeComponent({
      component: DateRangeComponent,
      inputs: ['mode', 'initialRangeType', 'required', 'disabled', 'minDate', 'maxDate', 'value'],
      outputs: ['valueChange'],
    }),
  );

  // FormInputComponent - migrated from the AngularJS `formInput` attribute directive
  // Usage in AngularJS: <acme-form-input name="email" label="Email"
  //                                      [valid-test]="$ctrl.form.email.$valid"
  //                                      error-message="Please enter a valid email">
  //                       <input id="email" data-ng-model="$ctrl.model.email">
  //                     </acme-form-input>
  // Note: the original was an attribute directive (`<div data-form-input …>`);
  // the downgrade is an element directive, so call sites need to switch to
  // `<acme-form-input>` when they adopt it. The legacy attribute directive
  // remains in apps/b2b-ui/common/directives/formInput/ until consumers move.
  acmeModule.directive(
    'acmeFormInput',
    downgradeComponent({
      component: FormInputComponent,
      inputs: ['name', 'label', 'hideLabel', 'validTest', 'errorMessage',
               'isPanelGroup', 'removeLabel', 'srOnly'],
    }),
  );

  // InputFieldComponent - migrated from the AngularJS `inputField` attribute directive
  // Usage in AngularJS: <acme-input-field name="email" type="email" label="Email"
  //                                       model="$ctrl.email" on-model-change="$ctrl.email = $event"
  //                                       valid-test="$ctrl.isEmailValid"
  //                                       error-message="'Please enter a valid email'">
  //                     </acme-input-field>
  // Note: the original was an attribute directive (`<div data-input-field …>`);
  // the downgrade is an element directive, so call sites need to switch to
  // `<acme-input-field>` when they adopt it. The legacy attribute directive
  // remains in apps/b2b-ui/common/directives/inputField/ until consumers move.
  // `model`/`modelChange` replaces the legacy two-way `model='='`; the original
  // AJS directives `fieldFocus` and `numeric` are re-implemented inline.
  acmeModule.directive(
    'acmeInputField',
    downgradeComponent({
      component: InputFieldComponent,
      inputs: ['type', 'name', 'label', 'addon', 'icon', 'useFullWidth',
               'disabled', 'useCancel', 'setFieldFocus', 'fieldFocusTimeout',
               'maxLength', 'hasAddon', 'hideLabel', 'validTest', 'errorMessage',
               'isPanelGroup', 'placeholderValue', 'required', 'numericOnly',
               'model'],
      outputs: ['modelChange'],
    }),
  );

  // InputFieldWithLabelComponent - migrated from the `inputFieldWithLabel` AJS
  // attribute directive (apps/b2b-ui/common/directives/inputFieldWithLabel/).
  // The original was an attribute directive (`<div data-input-field-with-label …>`);
  // the downgrade is an element directive, so call sites switch to
  // `<acme-input-field-with-label>` when they adopt it. The legacy directive
  // remains registered until consumers move. `model`/`modelChange` replaces the
  // legacy two-way `model='='`; `fieldFocus`/`numeric` are re-implemented inline
  // and masking is handled inline via Maskito (`maskOptions`/`maskValue`).
  acmeModule.directive(
    'acmeInputFieldWithLabel',
    downgradeComponent({
      component: InputFieldWithLabelComponent,
      inputs: ['type', 'name', 'label', 'disabled', 'setFieldFocus',
               'fieldFocusTimeout', 'maxLength', 'hideLabel', 'required',
               'numericOnly', 'notifyOnBlur', 'validTest', 'errorMessage',
               'fieldName', 'errors', 'info', 'maskOptions', 'maskValue',
               'model'],
      outputs: ['modelChange', 'modelChanged', 'blurred'],
    }),
  );

  // PasswordRequirementsComponent - migrated from acme-password-requirements
  // Usage: <acme-password-requirements password="$ctrl.model.password"
  //                                  password-policy="$ctrl.passwordPolicy">
  //        </acme-password-requirements>

  acmeModule.directive(
    'acmePasswordRequirements',
    downgradeComponent({
      component: PasswordRequirementsComponent,
      inputs: ['password', 'passwordPolicy'],
    }),
  );

  // ReservationInputComponent - migrated from acme-reservation-input
  // Usage: <ng-reservation-input [value]="$ctrl.qty" [max]="ticket.quantity" [add-on]="'!'"
  //                              on-value-change="$ctrl.handleQty($event)"
  //                              on-reservation-error="$ctrl.handleErr($event)"
  //                              on-reservation-success="$ctrl.handleOk($event)">
  //        </ng-reservation-input>
  // Inputs must use `[kebab-name]="expr"` (or `bind-kebab-name`): a plain
  // `value="$ctrl.qty"` attribute reaches the component as the literal string
  // "$ctrl.qty" (see DEV-14076).
  // The reservation/timeout machinery (ENG-1199) is dormant in the migrated
  // component, but `reservationError` and `reservationSuccess` outputs are
  // exposed so consumer templates can pre-wire their handlers; they will
  // start emitting once ENG-1199 is reverted. `onBeforeTimeout` was dropped.
  const reservationInputDowngrade = downgradeComponent({
    component: ReservationInputComponent,
    inputs: ['max', 'min', 'ignoreValue', 'value', 'addOn', 'eventId', 'label'],
    outputs: ['valueChange', 'reservationError', 'reservationSuccess'],
  });
  acmeModule.directive('ngReservationInput', reservationInputDowngrade);
  acmeModule.directive('acmeReservationInput', reservationInputDowngrade);

  // TicketTypeSelectorComponent - migrated from acme-ticket-type-selector
  // Usage in AngularJS: <acme-ticket-type-selector event-id="..." items="..."
  //   on-select="ctrl.add($event.selectedItem)"></acme-ticket-type-selector>
  // The emitted payload preserves the legacy { selectedItem } wrapper so
  // existing `$event.selectedItem` consumer expressions keep working. The
  // legacy AngularJS `combobox` typeahead is replaced by a native dropdown.
  const ticketTypeSelectorDowngrade = downgradeComponent({
    component: TicketTypeSelectorComponent,
    inputs: ['items', 'eventId', 'label', 'disabled'],
    outputs: ['select'],
  });
  acmeModule.directive('ngTicketTypeSelector', ticketTypeSelectorDowngrade);
  acmeModule.directive('acmeTicketTypeSelector', ticketTypeSelectorDowngrade);

  // AddonTypeSelectorComponent - migrated from acme-addon-type-selector
  // Usage in AngularJS: <acme-addon-type-selector event-id="..." [items]="..."
  //   on-select="ctrl.add($event.selectedItem)"></acme-addon-type-selector>
  // Thin wrapper around acme-combobox; emits { selectedItem } to keep legacy
  // `$event.selectedItem` consumer expressions working unchanged.
  const addonTypeSelectorDowngrade = downgradeComponent({
    component: AddonTypeSelectorComponent,
    inputs: ['items', 'eventId', 'label', 'disabled'],
    outputs: ['select'],
  });
  acmeModule.directive('ngAddonTypeSelector', addonTypeSelectorDowngrade);
  acmeModule.directive('acmeAddonTypeSelector', addonTypeSelectorDowngrade);

  // ExampleComponent - example Angular view for routing demo
  // Usage: <ng-example></ng-example>
  // This is used in UI-Router state definitions for named views
  acmeModule.directive('ngExample', downgradeComponent({ component: ExampleComponent }));

  // SpinnerComponent - migrated from the AngularJS `spinner` directive
  // Usage: <ng-spinner spinner-hide="$ctrl.isLoaded"></ng-spinner>
  acmeModule.directive('ngSpinner', downgradeComponent({ component: SpinnerComponent, inputs: ['spinnerHide'] }));

  // HeaderMinimalComponent - migrated minimal header mode (composes app-header-shell).
  // No inputs/outputs: it derives its state from the Angular auth/feature/themes services.
  // Usage: <ng-header-minimal></ng-header-minimal>
  acmeModule.directive('ngHeaderMinimal', downgradeComponent({ component: HeaderMinimalComponent }));

  // HeaderPrintComponent - migrated print header mode (composes app-header-shell).
  // Two-way `hideItems` model + `print` output. `print` reaches AppController via
  // PrintService; `hideItemsChange` is bound to OrdersPrintController in the layout.
  // Usage: <ng-header-print on-hide-items-change="ordersPrint.onHideItemsUpdate($event)"></ng-header-print>
  // NOTE: outputs must use the `on-`/`(...)` form — ngUpgrade does NOT bind the
  // plain `hide-items-change` attribute for downgraded component outputs.
  acmeModule.directive(
    'ngHeaderPrint',
    downgradeComponent({
      component: HeaderPrintComponent,
      inputs: ['hideItems'],
      outputs: ['hideItemsChange', 'print'],
    }),
  );

  // HeaderAppComponent - migrated application header chrome + non-cart nav
  // (composes app-header-shell). Derives state from auth/feature/themes services;
  // the AngularJS <header-cart> is projected into its <ng-content>.
  // Usage: <ng-header-app><header-cart></header-cart></ng-header-app>
  acmeModule.directive('ngHeaderApp', downgradeComponent({ component: HeaderAppComponent }));

  // ZendeskLoginComponent - Zendesk SSO login page (DEV-11861)
  // Usage: <ng-zendesk-login></ng-zendesk-login>
  // Mounted by the /zendesk/login ui-router state.
  acmeModule.directive(
    'ngZendeskLogin',
    downgradeComponent({ component: ZendeskLoginComponent }),
  );

  // ZendeskLogoutComponent - Zendesk SSO logout page (DEV-11861)
  // Usage: <ng-zendesk-logout></ng-zendesk-logout>
  // Mounted by the /zendesk/logout ui-router state.
  acmeModule.directive(
    'ngZendeskLogout',
    downgradeComponent({ component: ZendeskLogoutComponent }),
  );

  // GiftCardsComponent - Gift Cards product config settings page (DEV-13071)
  // Usage: <ng-gift-cards></ng-gift-cards>
  // Mounted by the application.giftCards ui-router state (apps/b2b-ui/views/application/application.ts),
  // reached via the "Gift Cards" nav item added by ApplicationController once
  // the stored_value_activate extendable resolves (DEV-13600).
  acmeModule.directive(
    'ngGiftCards',
    downgradeComponent({ component: GiftCardsComponent }),
  );

  // Uncomment as more components are migrated:
  // acmeModule.directive('ngLoader', downgradeComponent({ component: LoaderComponent }));

  // ============================================
  // Downgraded Services
  // ============================================

  // AuthenticationService (Angular 18) — registered under both legacy names
  // so the 53+ existing AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/auth/auth.service.js + authentication.service.ts
  const authFactory = downgradeInjectable(AuthenticationService);
  acmeModule.factory('AuthenticationService', authFactory);
  acmeModule.factory('AuthService', authFactory);

  // PermissionsService (Angular 18) — registered under the legacy name so
  // the 112 existing AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/permissions/permissions.service.js
  acmeModule.factory('PermissionsService', downgradeInjectable(PermissionsService));

  // NavigationService (Angular 18) — registered under the legacy name so the
  // existing AngularJS consumers in b2b-ui receive it transparently.
  // Replaces apps/b2b-ui/common/services/navigation/navigation.service.ts
  acmeModule.factory('NavigationService', downgradeInjectable(NavigationService));

  // ApiService (Angular 18 facade) — registered under the legacy name so the
  // 396 existing AngularJS consumers (`apiService.user.login(...)`) keep
  // working without changes. The facade exposes UserApiService and
  // SystemApiService directly; the remaining 55 sub-services are exposed as
  // lazy bridges to the AngularJS legacy services until each one is migrated.
  // Replaces apps/b2b-ui/common/services/api/api.service.js
  acmeModule.factory('ApiService', downgradeInjectable(ApiService));

  // FeatureToggleService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/featureToggle/featureToggle.service.js
  acmeModule.factory('FeatureToggleService', downgradeInjectable(FeatureToggleService));

  // ConfigService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/config/config.service.js
  acmeModule.factory('ConfigService', downgradeInjectable(ConfigService));

  // TenantService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/tenant/tenant.service.js
  acmeModule.factory('TenantService', downgradeInjectable(TenantService));

  // StorageService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/storage/storage.service.js
  acmeModule.factory('StorageService', downgradeInjectable(StorageService));

  // UtilsService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/helpers/utils.service.js
  acmeModule.factory('UtilsService', downgradeInjectable(UtilsService));

  // LoggerService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/logger/logger.service.js
  acmeModule.factory('LoggerService', downgradeInjectable(LoggerService));

  // CalendarService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/calendar/calendar.service.js
  acmeModule.factory('CalendarService', downgradeInjectable(CalendarService));

  // HelpersService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 2 blocker.
  // Replaces apps/b2b-ui/common/services/helpers/helpers.service.js
  acmeModule.factory('HelpersService', downgradeInjectable(HelpersService));

  // ThemesHelperService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 2 blocker.
  // Replaces apps/b2b-ui/common/services/themesHelper/themesHelper.service.js
  acmeModule.factory('ThemesHelperService', downgradeInjectable(ThemesHelperService));

  // UserPreferencesService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 2 blocker.
  // Replaces apps/b2b-ui/common/services/userPreferences/userPreferences.service.js
  acmeModule.factory('UserPreferencesService', downgradeInjectable(UserPreferencesService));

  // BugsnagService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/bugsnag/bugsnag.service.ts
  acmeModule.factory('BugsnagService', downgradeInjectable(BugsnagService));

  // PendoService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/pendo/pendo.service.ts
  acmeModule.factory('PendoService', downgradeInjectable(PendoService));

  // LunchLitelyService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/lunchLitely/lunchLitely.service.ts
  acmeModule.factory('LunchLitelyService', downgradeInjectable(LunchLitelyService));

  // ButtonGroupActionsService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/button-group-actions/button-group-actions.service.ts
  acmeModule.factory('ButtonGroupActionsService', downgradeInjectable(ButtonGroupActionsService));

  // ViewStateService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/viewState/viewState.service.js
  acmeModule.factory('ViewStateService', downgradeInjectable(ViewStateService));

  // DatePickerNoteService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/datePickerNote/datePickerNote.service.js
  acmeModule.factory('DatePickerNoteService', downgradeInjectable(DatePickerNoteService));

  // ValidateService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Depends on the legacy
  // ThemesHelperService via the THEMES_HELPER_PORT bridge.
  // Replaces apps/b2b-ui/common/services/validate/validate.service.js
  acmeModule.factory('ValidateService', downgradeInjectable(ValidateService));

  // GanttTransformService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/ganttTransform/ganttTransform.service.js
  acmeModule.factory('GanttTransformService', downgradeInjectable(GanttTransformService));

  // SidebarService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/sidebar/sidebar.service.ts
  acmeModule.factory('SidebarService', downgradeInjectable(SidebarService));

  // ContentHeaderService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently.
  // Replaces apps/b2b-ui/common/services/contentHeader/contentHeader.service.ts
  acmeModule.factory('ContentHeaderService', downgradeInjectable(ContentHeaderService));

  // ─── Wave 4: Checkout Chain Services ───────────────────────────────────────

  // DiscountsService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 4 isolated service.
  // Replaces apps/b2b-ui/common/services/discounts/discounts.service.js
  acmeModule.factory('DiscountsService', downgradeInjectable(DiscountsService));

  // StripeService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 4 isolated service.
  // Replaces apps/b2b-ui/common/services/stripe/stripe.service.js
  acmeModule.factory('StripeService', downgradeInjectable(StripeService));

  // PaymentProcessorService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently. Wave 4 isolated service.
  // Replaces apps/b2b-ui/common/services/paymentProcessor/paymentProcessor.service.js
  acmeModule.factory('PaymentProcessorService', downgradeInjectable(PaymentProcessorService));

  // OrderService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 4 isolated service.
  // Replaces apps/b2b-ui/common/services/orderService/orderService.service.js
  acmeModule.factory('OrderService', downgradeInjectable(OrderService));

  // PaymentSettingsService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently. Wave 4 Tier 1 service.
  // Replaces apps/b2b-ui/common/services/paymentSettings/paymentSettings.service.js
  acmeModule.factory('PaymentSettingsService', downgradeInjectable(PaymentSettingsService));

  // SalesRulesService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 4 Tier 1 service (1091 LOC).
  // Replaces apps/b2b-ui/common/services/salesRules/salesRules.service.js
  acmeModule.factory('SalesRulesService', downgradeInjectable(SalesRulesService));

  // OrderEditSalesRulesService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently. Wave 4 Tier 1 service.
  // Replaces apps/b2b-ui/common/services/salesRules/order-edit-sales-rules.service.ts
  acmeModule.factory('OrderEditSalesRulesService', downgradeInjectable(OrderEditSalesRulesService));

  // ─── Wave 4: Cart sub-services + Main chain ─────────────────────────────────

  // CartCouponsService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 4 Cart sub-service.
  // Replaces apps/b2b-ui/common/services/cart/coupons/coupons.service.ts
  acmeModule.factory('CartCouponsService', downgradeInjectable(CartCouponsService));

  // CartEventsService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 4 Cart sub-service.
  // Replaces apps/b2b-ui/common/services/cart/events/events.service.ts
  acmeModule.factory('CartEventsService', downgradeInjectable(CartEventsService));

  // CartMembershipsService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently. Wave 4 Cart sub-service.
  // Replaces apps/b2b-ui/common/services/cart/memberships/memberships.service.ts
  acmeModule.factory('CartMembershipsService', downgradeInjectable(CartMembershipsService));

  // CartService (Angular 18) — registered under the legacy name so the 28+
  // existing AngularJS consumers receive it transparently. Wave 4 Main chain.
  // ⚠️ PITFALL #7: cart.id null handling — see cart.service.ts for details.
  // Replaces apps/b2b-ui/common/services/cart/cart.service.ts
  acmeModule.factory('CartService', downgradeInjectable(CartService));

  // PaymentService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 4 Main chain.
  // Replaces apps/b2b-ui/common/services/payment/payment.service.ts
  acmeModule.factory('PaymentService', downgradeInjectable(PaymentService));

  // CheckoutProcessService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently. Wave 4 Main chain.
  // Replaces apps/b2b-ui/common/services/checkout/checkoutProcess.service.ts
  acmeModule.factory('CheckoutProcessService', downgradeInjectable(CheckoutProcessService));

  // ─── Wave 3: Secondary domain services ──────────────────────────────────────

  // ResourcesService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 3 item #22. Migrated ahead
  // of auditTrail so AuditTrailConfigsService can inject it directly (no port).
  // Replaces apps/b2b-ui/common/services/resources/resources.service.js
  acmeModule.factory('ResourcesService', downgradeInjectable(ResourcesService));

  // TemplatesHelpersService (Angular 18) — registered under the legacy name so
  // existing AngularJS consumers receive it transparently. Wave 3 (nested under
  // templates #19). Migrated before TemplatesService/FlexpayService which depend on it.
  // Replaces apps/b2b-ui/common/services/templates/helpers/helpers.service.js
  acmeModule.factory('TemplatesHelpersService', downgradeInjectable(TemplatesHelpersService));

  // FlexpayService (Angular 18) — registered under the legacy name so existing
  // AngularJS consumers receive it transparently. Wave 3 (nested under templates #19).
  // Replaces apps/b2b-ui/common/services/templates/flexpay/flexpay.service.js
  acmeModule.factory('FlexpayService', downgradeInjectable(FlexpayService));

  // TemplatesService (Angular 18) — registered under the legacy name so the 45+
  // existing AngularJS consumers receive it transparently. Wave 3 item #19.
  // Replaces apps/b2b-ui/common/services/templates/templates.service.js
  acmeModule.factory('TemplatesService', downgradeInjectable(TemplatesService));

  // AuditTrail family (Angular 18) — registered under their legacy names so the
  // auditTrail/history/printerFriendlyLink directives receive them transparently.
  // Wave 3 item #14. Depends on the (now migrated) Templates/Flexpay/Resources.
  // Replaces apps/b2b-ui/common/services/auditTrail/**.
  acmeModule.factory('AuditTrailVersionHandlerService', downgradeInjectable(AuditTrailVersionHandlerService));
  acmeModule.factory('AuditTrailColumnGenerator', downgradeInjectable(AuditTrailColumnGenerator));
  acmeModule.factory('AuditTrailConfigsService', downgradeInjectable(AuditTrailConfigsService));
  acmeModule.factory('AuditTrailGridsService', downgradeInjectable(AuditTrailGridsService));
  acmeModule.factory('AuditTrailService', downgradeInjectable(AuditTrailService));

  // TicketConfigService / TerminalsService / VenueService (Angular 18) —
  // registered under their legacy names. Wave 3 items #18, #20, #21
  // (no-dependency pure-logic services).
  // Replaces apps/b2b-ui/common/services/{ticketConfig,terminals,venue}/*.service.js
  acmeModule.factory('TicketConfigService', downgradeInjectable(TicketConfigService));
  acmeModule.factory('TerminalsService', downgradeInjectable(TerminalsService));
  acmeModule.factory('VenueService', downgradeInjectable(VenueService));

  // RulesEngineService (Angular 18) — registered under the legacy name. Wave 3
  // item #15. Membership-lifecycle rule logic; depends only on HelpersService✅.
  // Replaces apps/b2b-ui/common/services/rulesEngine/rulesEngine.service.js
  acmeModule.factory('RulesEngineService', downgradeInjectable(RulesEngineService));

  // ContactService (Angular 18) — registered under the legacy name. Wave 3
  // item #24. In-memory contact-items state; the legacy ValidateService dep was
  // declared but unused, so it's dropped from the class.
  // Replaces apps/b2b-ui/common/services/contact/contact.service.js
  acmeModule.factory('ContactService', downgradeInjectable(ContactService));

  // CustomerService (Angular 18) — registered under the legacy name. Wave 3
  // item #23. Dependency-free helper: customer model shape + salutation lookup.
  // Replaces apps/b2b-ui/common/services/customer/customer.service.ts
  acmeModule.factory('CustomerService', downgradeInjectable(CustomerService));

  // ModalService (Angular 18) — registered under the legacy name. Wave 3 item
  // #27. Thin wrapper over AngularJS $uibModal, reached via UIB_MODAL_PORT
  // (wired in core.module.ts).
  // Replaces apps/b2b-ui/common/services/modal/modal.service.ts
  acmeModule.factory('ModalService', downgradeInjectable(ModalService));

  // AddressService / LocationService (Angular 18) — registered under their
  // legacy names. Wave 3 item #25. AddressService is dependency-free; the
  // separate LocationService injects the Angular 18 ApiService directly.
  // Replaces apps/b2b-ui/common/services/address/{address,location}.service.ts
  acmeModule.factory('AddressService', downgradeInjectable(AddressService));
  acmeModule.factory('LocationService', downgradeInjectable(LocationService));

  // FeatureService (Angular 18) — registered under the legacy name. Wave 3 item
  // #26. Aggregates the feature sub-services (objectLock/customers/coupons,
  // injected directly as Angular 18 classes) and resolves flags via ThemesHelper✅.
  // The sub-services are internal to FeatureService and are NOT injected by name
  // anywhere in AngularJS, so they are not separately downgrade-registered; their
  // legacy module shells stay empty for the FeatureServiceModule dependency graph.
  // Replaces apps/b2b-ui/common/services/feature/**.
  acmeModule.factory('FeatureService', downgradeInjectable(FeatureService));

  // Membership family (Angular 18) — registered under their legacy names so the
  // membership/cart/subscriptions controllers and directives that inject these
  // by name receive them transparently. Wave 3 item #28.
  //   - MembershipService: in-memory model + purchase-type/sales-channel helpers;
  //     deps HelpersService/CalendarService/PermissionsService✅ + FILTER/ROOT_SCOPE ports.
  //   - MembershipOfferingService: offering create/edit staging; injects the
  //     Angular 18 MembershipService + ApiService directly.
  //   - MembershipCardholderService: already-TS; injects ModalService✅/AddressService✅.
  // Replaces apps/b2b-ui/common/services/membership/**. Removes the former
  // MEMBERSHIP_SERVICE_PORT bridge (CartMembershipsService now injects the class).
  acmeModule.factory('MembershipService', downgradeInjectable(MembershipService));
  acmeModule.factory('MembershipOfferingService', downgradeInjectable(MembershipOfferingService));
  acmeModule.factory('MembershipCardholderService', downgradeInjectable(MembershipCardholderService));

  // ReportsService (Angular 18) — registered under the legacy name. Wave 3 item
  // #16. Report parsing/subtotal/group-summary logic; delegates printable-markup
  // to the internal ReportsGeneratorService (Angular 18, injected directly, not
  // downgrade-registered). deps Q/TIMEOUT/FILTER ports + Helpers/Validate/Calendar✅.
  // Replaces apps/b2b-ui/common/services/reports/**.
  acmeModule.factory('ReportsService', downgradeInjectable(ReportsService));

  // PosConfigService (Angular 18) — registered under the legacy name. Wave 3 item
  // #17. Orchestrates the six POS config sub-services (options/images/tiles/pages/
  // navigation/settings) exposed as properties; the sub-services are internal,
  // injected directly as Angular 18 classes and NOT downgrade-registered. The
  // legacy `uuid2` dep is replaced with native crypto.randomUUID(). deps Helpers✅
  // + FILTER port. Replaces apps/b2b-ui/common/services/posConfig/**.
  acmeModule.factory('PosConfigService', downgradeInjectable(PosConfigService));

  // Provider-style API microservices migrated to Angular 18. Each one is
  // registered under its legacy AngularJS name so the existing AJS
  // controllers/directives that inject these services by name keep working.
  // The legacy `.config()` block in apps/b2b-ui/common/services/api/api.service.js
  // that called `setEndpoint(...)` was removed; the Angular 18 implementations
  // hardcode their microservice URLs.
  //
  // Replaces:
  //   apps/b2b-ui/common/services/api/import/importJobsApi.service.js
  //   apps/b2b-ui/common/services/api/import/importJobExecutionsApi.service.js
  //   apps/b2b-ui/common/services/api/import/importExportsApi.service.js
  //   apps/b2b-ui/common/services/api/offline/offline.service.js
  //   apps/b2b-ui/common/services/posCloudConfiguration/posCloudConfiguration.service.js
  acmeModule.factory('ImportJobsApiService', downgradeInjectable(ImportJobsApiService));
  acmeModule.factory('ImportJobExecutionsApiService', downgradeInjectable(ImportJobExecutionsApiService));
  acmeModule.factory('ImportExportsApiService', downgradeInjectable(ImportExportsApiService));
  acmeModule.factory('OfflineService', downgradeInjectable(OfflineService));
  acmeModule.factory('PosCloudConfigurationService', downgradeInjectable(PosCloudConfigurationService));

  // PrintService (Angular 18) — registered under the legacy name so the
  // AngularJS AppController can subscribe to print requests from the migrated
  // print header (replaces the `Header::print` $scope event).
  acmeModule.factory('PrintService', downgradeInjectable(PrintService));

  console.log(
    '[ngUpgrade] Downgraded components registered: ngAlert, acmeAlert, ngCheckbox, ngDateRange, acmeFormInput, acmeInputField, ngPasswordRequirements, acmePasswordRequirements, ngReservationInput, acmeReservationInput, ngTicketTypeSelector, acmeTicketTypeSelector, ngAddonTypeSelector, acmeAddonTypeSelector, ngExample, ngSpinner, ngHeaderMinimal, ngHeaderPrint, ngHeaderApp, ngZendeskLogin, ngZendeskLogout, ngGiftCards',
  );
  console.log(
    '[ngUpgrade] Downgraded services registered: AuthenticationService, AuthService, PermissionsService, NavigationService, ApiService, FeatureToggleService',
  );
}

/**
 * Example: How to downgrade an Angular component
 *
 * 1. Create the Angular component:
 * ```typescript
 * @Component({
 *   selector: 'app-my-component',
 *   template: `<div>{{message}}</div>`
 * })
 * export class MyComponent {
 *   @Input() message: string;
 *   @Output() clicked = new EventEmitter<void>();
 * }
 * ```
 *
 * 2. Add to SharedModule declarations and exports
 *
 * 3. Register here:
 * ```typescript
 * acmeModule.directive(
 *   'ngMyComponent',
 *   downgradeComponent({
 *     component: MyComponent,
 *     inputs: ['message'],
 *     outputs: ['clicked']
 *   })
 * );
 * ```
 *
 * 4. Use in AngularJS template:
 * ```html
 * <ng-my-component
 *   message="ctrl.message"
 *   on-clicked="ctrl.handleClick()">
 * </ng-my-component>
 * ```
 */
